// src/pages/Promos.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";
import toast, { Toaster } from "react-hot-toast";
import { useUserContext } from "../contexts/UserContext";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { API_URL } from '../config/api';

export default function Promos() {
  const { user } = useUserContext();
  const [loading, setLoading] = useState(true);
  const [promos, setPromos] = useState([]);
  const [history, setHistory] = useState([]);
  const [editingPromo, setEditingPromo] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [promoToDelete, setPromoToDelete] = useState(null);

  const [form, setForm] = useState({
    code: "",
    type: "percentage",
    value: "",
    minAmount: 0,
    expiresAt: "",
    description: "", // 🔹 Champ description ajouté
  });

  // 🔹 Gestion des modales
  const openEditModal = (promo) => setEditingPromo({ ...promo });
  const closeEditModal = () => setEditingPromo(null);

  const openDeleteModal = (promo) => {
  setPromoToDelete(promo);
  setShowDeleteModal(true);
};

const closeDeleteModal = () => {
  setPromoToDelete(null);
  setShowDeleteModal(false);
};

  // 🔹 Génération automatique d'un code promo
  const generatePromoCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "PROMO-";
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setForm((p) => ({ ...p, code }));
  };

  // 🔹 Chargement des promos depuis le backend
  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API_URL}/promos`);
      const now = new Date();

      const active = res.data.filter((p) => new Date(p.expiresAt) >= now);
      const expired = res.data.filter((p) => new Date(p.expiresAt) < now);

      setPromos(active);
      setHistory(expired);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des promos.");
    }
  };

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetchPromos().finally(() => setLoading(false));
  }, [user]);

  // 🔹 Gestion du formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value || !form.expiresAt) return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/code/promo`, form);
      toast.success("Code promo ajouté !");
      setForm({ code: "", type: "percentage", value: "", minAmount: 0, expiresAt: "", description: "" });
      await fetchPromos();
    } catch (err) {
      toast.error("Erreur lors de l'ajout.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Suppression d'un code promo
const deletePromo = async () => {
  if (!promoToDelete) return;

  try {
    await axios.delete(`${API_URL}/promos/${promoToDelete._id}`);
    toast.success("Code promo supprimé.");
    fetchPromos();
  } catch (e) {
    toast.error("Erreur suppression.");
  } finally {
    closeDeleteModal();
  }
};

  // 🔹 Mise à jour d'un code promo
  const handleUpdatePromo = async () => {
    try {
      await axios.put(
        `${API_URL}/update/code/promo/${editingPromo._id}`,
        {
          code: editingPromo.code,
          type: editingPromo.type,
          value: editingPromo.value,
          minAmount: editingPromo.minAmount,
          expiresAt: editingPromo.expiresAt,
          description: editingPromo.description || "",
        }
      );
      toast.success("Code promo mis à jour");
      closeEditModal();
      fetchPromos();
    } catch (err) {
      console.error(err);
      toast.error("Erreur mise à jour");
    }
  };

  if (loading) return <div>Chargement…</div>;

  return (
    <>
      <Navbar />
      <div className="container">
        <Toaster />
        <h1>Codes Promos</h1>

        {/* ------------------ Tableau des promos ------------------ */}
        <h2 style={{ marginTop: 40 }}>Historique des codes promos</h2>
        {promos.concat(history).length === 0 ? (
          <p>Aucun code promo disponible.</p>
        ) : (
          <table className="promo-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Valeur</th>
                <th>Description</th>
                <th>Min Amount</th>
                <th>Statut</th>
                <th>Expiré le</th>
                <th>Créé le</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.concat(history).map((promo) => {
                const status = new Date(promo.expiresAt) < new Date() ? "Expiré" : "Actif";
                return (
                  <tr key={promo._id}>
                    <td>{promo.code}</td>
                    <td>{promo.type}</td>
                    <td>{promo.type === "percentage" ? `${promo.value}%` : `${promo.value} FCFA`}</td>
                    <td>{promo.description || "-"}</td> {/* 🔹 Affichage description */}
                    <td>{promo.minAmount.toLocaleString()} FCFA</td>
                    <td style={{ fontWeight: "bold", color: status === "Actif" ? "#2563eb" : "#b30000" }}>
                      {status}
                    </td>
                    <td>{new Date(promo.expiresAt).toLocaleDateString()}</td>
                    <td>{new Date(promo.createdAt).toLocaleDateString()}</td>
                    <td className="actions">
                      <FiEdit className="icon edit" onClick={() => openEditModal(promo)} />
                     <FiTrash2 className="icon delete" onClick={() => openDeleteModal(promo)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* ------------------ Formulaire d’ajout ------------------ */}
        <form className="promo-form" onSubmit={handleSubmit}>
          <div className="form-row inline">
            <input
              type="text"
              name="code"
              value={form.code}
              onChange={handleChange}
              placeholder="EX: PROMO10"
              required
            />
            <button type="button" className="btn secondary" onClick={generatePromoCode}>
              Générer
            </button>
          </div>

          <div className="form-row">
            <label>Type</label>
            <select name="type" value={form.type} onChange={handleChange}>
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (FCFA)</option>
            </select>
          </div>

          <div className="form-row">
            <label>{form.type === "percentage" ? "Pourcentage (%)" : "Montant (FCFA)"}</label>
            <input type="number" name="value" value={form.value} onChange={handleChange} required />
          </div>

          <div className="form-row">
            <label>Description (optionnel)</label>
            <input type="text" name="description" value={form.description} onChange={handleChange} placeholder="Ex: Remise spéciale Noël" />
          </div>

          <div className="form-row">
            <label>Montant minimum (optionnel)</label>
            <input type="number" name="minAmount" value={form.minAmount} onChange={handleChange} />
          </div>

          <div className="form-row">
            <label>Date d’expiration</label>
            <input type="date" name="expiresAt" value={form.expiresAt} onChange={handleChange} required />
          </div>

          <button type="submit" className="btn save">Ajouter le code promo</button>
        </form>

        {/* ------------------ Modal édition ------------------ */}
        {editingPromo && (
          <div className="modal-backdrop">
            <div className="modal-update">
              <h3>Modifier le code promo</h3>
              {["code", "value", "minAmount", "description"].map((key) => (
                <div className="form-row" key={key}>
                  <label>
                    {key === "value" ? "Valeur" : key === "minAmount" ? "Montant minimum" : key === "description" ? "Description" : "Code"}
                  </label>
                  <input
                    type={key === "value" || key === "minAmount" ? "number" : "text"}
                    value={editingPromo[key] || ""}
                    onChange={(e) => setEditingPromo((p) => ({ ...p, [key]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="form-row">
                <label>Date d’expiration</label>
                <input
                  type="date"
                  value={editingPromo.expiresAt?.slice(0, 10)}
                  onChange={(e) => setEditingPromo((p) => ({ ...p, expiresAt: e.target.value }))}
                />
              </div>
              <div className="modal-actions">
                <button className="btn cancel" onClick={closeEditModal}>Annuler</button>
                <button className="btn save" onClick={handleUpdatePromo}>Enregistrer</button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && promoToDelete && (
  <div className="modal-backdrop">
    <div className="modal-update">
      <h3>Confirmer la suppression</h3>
      <p>Voulez-vous vraiment supprimer le code promo <strong>{promoToDelete.code}</strong> ?</p>
      <div className="modal-actions">
        <button className="btn cancel" onClick={closeDeleteModal}>Annuler</button>
        <button className="btn save" onClick={deletePromo}>Supprimer</button>
      </div>
    </div>
  </div>
)}
      </div>
      <Footer />
      <style>{`
        .promo-table { width: 100%; border-collapse: collapse; }
        .promo-table th { background: #f8fafc; font-weight: 600; padding: 12px; text-align: left; border-bottom: 1px solid #e5e7eb; color:red; }
        .actions { display: flex; gap: 12px; }
        .icon { cursor: pointer; font-size: 18px; }
        .icon.edit { color: #2563eb; }
        .icon.delete { color: #dc2626; }
        .btn.secondary { background: #f1f5f9; color: #111827; border: 1px solid #e5e7eb; }
        .form-row.inline { display: flex; gap: 10px; }
        .modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.modal-update {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 400px;
  max-width: 90%;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}
.modal-actions .btn.cancel { background: #ccc; }
.modal-actions .btn.save { background: #dc2626; color: #fff; }
      `}</style>
    </>
  );
}